import { action, observable, computed, runInAction } from 'mobx';
import { message } from 'antd';
import axios, { CancelTokenSource } from 'axios';
import { TestProjectRunResults, TestRunComparisonResult } from '../types';
import { TestProjectDashboardStore } from '.';
import { TestProjectService } from '../services';
import { Utils } from '../../common/services/Utils';
import { RouterStore } from '../../common/stores';
import { TestProjectsNavigation } from '../routes';

type ComparisonOwner = 'dashboard' | 'comparison';

type ComparisonRequest = {
    owner: ComparisonOwner;
    cancellation: CancelTokenSource;
};

export default class TestProjectRunCompareStore {
    @observable
    isLoading: boolean = false;

    @observable
    isSelectionMode: boolean = false;

    @observable
    selectedRunIds: Set<string> = new Set();

    @observable.ref
    private comparison: { key: string; result: TestRunComparisonResult } | undefined;

    private request: ComparisonRequest | undefined;

    constructor(
        private readonly dashboardStore: TestProjectDashboardStore,
        private readonly routerStore: RouterStore,
        private readonly service: TestProjectService
    ) {}

    @computed
    get comparisonResult() {
        return this.comparison?.result || null;
    }

    @computed
    get project() {
        return this.dashboardStore.currentProject;
    }

    @action.bound
    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    @action.bound
    setIsSelectionMode(isSelectionMode: boolean) {
        this.isSelectionMode = isSelectionMode;
    }

    @action.bound
    cancelSelectionMode() {
        this.setIsSelectionMode(false);
        this.selectedRunIds.clear();
    }

    @action.bound
    toggleRunSelection(runId: string) {
        if (this.selectedRunIds.has(runId)) {
            this.selectedRunIds.delete(runId);
            return;
        }

        if (this.selectedRunIds.size >= 2) {
            message.warning('You can only select up to 2 test runs for comparison');
            return;
        }

        this.selectedRunIds.add(runId);

        if (this.selectedRunIds.size === 2) {
            this.executeComparisonWithSelectedRuns();
        }
    }

    @action.bound
    compareWithSelectedRun(run: TestProjectRunResults) {
        this.setIsSelectionMode(true);
        this.selectedRunIds.clear();
        this.selectedRunIds.add(run.id);
    }

    @action.bound
    async compareWithPreviousRun(run: TestProjectRunResults) {
        const previousRun = this.getPreviousRun(run);

        if (!previousRun) {
            message.error('No previous run found');
            return;
        }

        const dataStore = this.dashboardStore.testProjectDataStore;

        try {
            this.selectedRunIds.add(run.id);
            this.selectedRunIds.add(previousRun.id);
            await this.executeComparison(run.id, previousRun.id, 'dashboard');
        } finally {
            if (dataStore === this.dashboardStore.testProjectDataStore) {
                this.selectedRunIds.clear();
            }
        }
    }

    @action.bound
    async matchCurrentRunCompareData(params: Record<string, string | undefined>) {
        this.cancelSelectionMode();
        this.dashboardStore.matchCurrentTestProjectData(params);
        const dataStore = this.dashboardStore.testProjectDataStore;

        const firstRunId = params.firstRunId;
        const secondRunId = params.secondRunId;

        if (!dataStore || !firstRunId || !secondRunId) {
            return;
        }

        const key = this.getComparisonKey(firstRunId, secondRunId);
        // Dashboard comparison navigates after success. Reuse that keyed result instead of restarting it on mount.
        const comparison =
            this.comparison?.key === key
                ? Promise.resolve(true)
                : this.executeComparison(firstRunId, secondRunId, 'comparison');

        await Promise.all([
            !this.dashboardStore.testRuns.length ? this.dashboardStore.loadTestRuns() : Promise.resolve(),
            comparison
        ]);
    }

    getPreviousRun(run: TestProjectRunResults): TestProjectRunResults | undefined {
        const previousRuns = this.dashboardStore.testRuns
            .filter(testRun => Utils.safeDateStringCompare(testRun.runTime, run.runTime) < 0)
            .sort((a, b) => Utils.safeDateStringCompare(b.runTime, a.runTime));

        return previousRuns[0];
    }

    goToDashboard() {
        this.dashboardStore.goToDashboard();
    }

    cancelRequest() {
        this.request?.cancellation.cancel();
        this.request = undefined;

        this.setIsLoading(false);
    }

    abortDashboard() {
        this.abort('dashboard');
    }

    abortComparisonPage() {
        this.abort('comparison');
    }

    @action.bound
    private async executeComparisonWithSelectedRuns() {
        if (this.selectedRunIds.size !== 2) {
            return;
        }

        const [firstRunId, secondRunId] = Array.from(this.selectedRunIds);
        const dataStore = this.dashboardStore.testProjectDataStore;

        try {
            this.setIsSelectionMode(false);
            await this.executeComparison(firstRunId, secondRunId, 'dashboard');
        } finally {
            if (dataStore === this.dashboardStore.testProjectDataStore) {
                this.selectedRunIds.clear();
            }
        }
    }

    @action.bound
    private async executeComparison(firstRunId: string, secondRunId: string, owner: ComparisonOwner): Promise<boolean> {
        const dataStore = this.dashboardStore.testProjectDataStore;

        if (!dataStore || dataStore.projectId !== this.project?.id) {
            return false;
        }

        this.cancelRequest();
        const request: ComparisonRequest = {
            owner,
            cancellation: axios.CancelToken.source()
        };

        this.request = request;
        const key = this.getComparisonKey(firstRunId, secondRunId);
        this.comparison = undefined;

        try {
            this.setIsLoading(true);

            const response = await this.service.compareTestProjectRuns(
                dataStore.projectId,
                dataStore.testProjectId,
                firstRunId,
                secondRunId,
                request.cancellation.token
            );

            if (this.request !== request || dataStore !== this.dashboardStore.testProjectDataStore) {
                return false;
            }

            if (!response.isOk()) {
                message.error(response.error?.data?.title || 'Failed to compare test runs');
                return false;
            }

            runInAction(() => {
                this.comparison = { key, result: response.value };
            });

            if (!this.comparisonResult?.changedTopicsByPackage.length) {
                message.info('No differences found between the two test runs');
                return true;
            }

            if (owner === 'dashboard') {
                this.navigateToComparePage(firstRunId, secondRunId);
            }

            return true;
        } catch (err) {
            if (this.request !== request || dataStore !== this.dashboardStore.testProjectDataStore) {
                return false;
            }

            console.error(err);
            message.error('An error occurred while comparing test runs');
            return false;
        } finally {
            if (this.request === request) {
                this.setIsLoading(false);
                this.request = undefined;
            }
        }
    }

    private getComparisonKey(firstRunId: string, secondRunId: string) {
        const dataStore = this.dashboardStore.testProjectDataStore;

        return JSON.stringify([dataStore?.projectId, dataStore?.testProjectId, firstRunId, secondRunId]);
    }

    private abort(owner: ComparisonOwner) {
        // A departing route may cancel only the request it started.
        if (this.request?.owner === owner) {
            this.cancelRequest();
        }

        this.cancelSelectionMode();
    }

    private navigateToComparePage(firstRunId: string, secondRunId: string) {
        const dataStore = this.dashboardStore.testProjectDataStore;

        if (!dataStore) {
            return;
        }

        const path = TestProjectsNavigation.TestProjectRunComparePage.replace(':projectId', dataStore.projectId)
            .replace(':testProjectId', dataStore.testProjectId)
            .replace(':firstRunId', firstRunId)
            .replace(':secondRunId', secondRunId);

        this.routerStore.push(path);
    }
}
